import React, { useState, useEffect, useRef } from "react";
import { Handle, Position, NodeProps, useReactFlow, useNodes } from "reactflow";
import {
  FlaskConical,
  Plus,
  Trash2,
  ChevronRight,
  Hash,
  Type,
  ToggleLeft,
  Calendar,
  Braces,
  List,
  Clock,
  Split,
} from "lucide-react";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@components/ui/dropdown-menu";
import Confirmation from "@components/common/ConfirmDialog";
import { toast } from "@components/ui/use-toast";
import { useQuery } from "react-query";
import { getConnectedInputs, getExecutionPath } from "../common/nodeDataFlow";
import ResponseViewer from "../common/ResponseViewer";
import NodeContentSection from "../common/NodeContentSection";
import { executeWorkflow, getConditions } from "../createPluginApis";
import { handlerStyle, unwrapJsonData } from "../common/commonData";
import {
  getNodeExecutionParameters,
  buildWorkflowPayload,
  handleNodeExecutionResults,
  findDownstreamNodes,
} from "../utils/nodeExecutionUtils";
import { getNodeBackendType } from "../utils/nodeTransformUtils";
import CollapsedNodeView from "../common/NodeHeaderView";
import NodeContextMenu, {
  useNodeContextMenu,
  createContextMenuItems,
} from "../common/NodeContextMenu";
import PaginatedInputViewer from "../common/PaginatedInputViewer";
import { FullscreenWrapper } from "../common/withFullscreen";
import { FullscreenToggleButton } from "../common/FullscreenToggleButton";
import { ValidatedTitleInput } from "../components/ValidatedTitleInput";
import ExpressionEditor from "../common/ExpressionEditor";
import { flattenObject } from "../utils/fieldHelpers";
import { useResizablePanels } from "../hooks/useResizablePanels";
import { PanelDivider } from "../common/PanelDivider";

interface Condition {
  id: string;
  leftValue: string;
  rightValue: any;
  operator: {
    type: string;
    operation: string;
  };
}

interface IfElseNodeData {
  label?: string;
  uid: string;
  title: string;
  description?: string;
  conditions?: {
    options: {
      caseSensitive: boolean;
      leftValue: string;
      typeValidation: string;
      version: number;
    };
    conditions: Condition[];
  };
  looseTypeValidation?: boolean;
  options?: Record<string, any>;
  onDelete?: (id: string) => void;
  onDataChange?: (id: string, updates: any) => void;
  borderColor?: string;
  shadowColor?: string;
}

const IfElseNode: React.FC<NodeProps<IfElseNodeData>> = ({ id, data }) => {
  const label = data.label || "If Else";
  const [uid, setUid] = useState(data.uid || "");
  const [title, setTitle] = useState(data.title || "");
  const [description, setDescription] = useState(data.description || "");
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isTitleValid, setIsTitleValid] = useState(true);

  // Context menu
  const {
    showContextMenu,
    contextMenuPosition,
    handleContextMenu,
    closeContextMenu,
  } = useNodeContextMenu(id);

  // IF ELSE specific state
  const [conditions, setConditions] = useState<{
    options: {
      caseSensitive: boolean;
      leftValue: string;
      typeValidation: string;
      version: number;
    };
    conditions: Condition[];
  }>(
    data.conditions || {
      options: {
        caseSensitive: true,
        leftValue: "",
        typeValidation: "strict",
        version: 3,
      },
      conditions: [],
    },
  );

  const [looseTypeValidation] = useState(data.looseTypeValidation || false);

  // Combinator state for conditions (and/or) - initialize from data or default to and
  const [combinator, setCombinator] = useState<"and" | "or">(
    (data.conditions as any)?.combinator || "and",
  );

  // Input/Output state
  const [inputData, setInputData] = useState<any[]>([]);
  const [outputData, setOutputData] = useState<any>(null);
  const [errorResponse, setErrorResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Local state for node_data to track true/false item counts
  const [nodeData, setNodeData] = useState(
    (data as any).node_data || [
      { id: "true", outputKey: "True", itemCount: 0 },
      { id: "false", outputKey: "False", itemCount: 0 },
    ],
  );
  const nodes = useNodes();
  const reactFlowInstance = useReactFlow();
  const { getNodes, getEdges } = useReactFlow();
  const { containerRef, widths, startDrag } = useResizablePanels([
    33.33, 33.33, 33.34,
  ]);

  // Fetch condition operators
  const {
    data: operatorsData,
    isLoading: operatorsLoading,
    error: operatorsError,
  } = useQuery("conditionOperators", getConditions, {
    staleTime: Infinity, // Cache the operators data indefinitely
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    onError: (error: any) => {
      console.error("Failed to fetch condition operators:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load condition operators",
        duration: 3000,
      });
    },
  });

  // Auto-generate UID if not present
  useEffect(() => {
    if (!uid) {
      const newUid = `ifelse_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      setUid(newUid);
    }
  }, []);

  // Ensure initial node_data is synced to parent immediately on mount
  useEffect(() => {
    if (data.onDataChange && !(data as any).node_data) {
      // Parent doesn't have node_data yet, send initial state
      data.onDataChange(id, {
        node_data: nodeData,
      });
    }
  }, []); // Run once on mount

  // Load saved parameters from data prop (when loading from backend)
  useEffect(() => {
    const params = (data as any).storedParameters || {};

    if (params.conditions) {
      setConditions(params.conditions);
      if (params.conditions.combinator) {
        setCombinator(params.conditions.combinator);
      }
    }
  }, []); // Only run once on mount

  // Sync nodeData from parent when it changes (e.g., loading from backend)
  useEffect(() => {
    const parentNodeData = (data as any).node_data;
    if (
      parentNodeData &&
      Array.isArray(parentNodeData) &&
      parentNodeData.length > 0
    ) {
      // Only update if it's actually different to avoid loops
      const currentStr = JSON.stringify(nodeData);
      const parentStr = JSON.stringify(parentNodeData);
      if (currentStr !== parentStr) {
        setNodeData(parentNodeData);
      }
    }
  }, [(data as any).node_data]);

  // Refresh input data when node connections change
  useEffect(() => {
    refreshInputData();
    const interval = setInterval(refreshInputData, 3000);
    return () => clearInterval(interval);
  }, [id]);

  // Sync state to parent with storedParameters
  useEffect(() => {
    if (!data.onDataChange) return;

    // Build storedParameters for backend
    const conditionsWithCombinator = {
      ...conditions,
      combinator,
    };

    const storedParameters = {
      conditions: conditionsWithCombinator,
      looseTypeValidation,
      options: data.options || {},
      node_data: nodeData,
    };

    data.onDataChange(id, {
      uid,
      title,
      description,
      conditions: conditionsWithCombinator,
      looseTypeValidation,
      options: data.options || {},
      outputData,
      response: outputData,
      sub_type: "if_else",
      node_type: "flow",
      storedParameters,
      node_data: nodeData,
    });
  }, [
    uid,
    title,
    description,
    conditions,
    combinator,
    looseTypeValidation,
    outputData,
    data.options,
  ]);

  // Ref to track previous outputData for sync effect
  const prevOutputDataRef = useRef<any>(null);

  // Sync outputData from node data (updated during workflow execution)
  // Use deep comparison to prevent unnecessary updates
  useEffect(() => {
    const newOutputData = (data as any).outputData || (data as any).response;

    // Skip if no new data
    if (!newOutputData) {
      // Clear output if explicitly set to null/undefined
      if (outputData !== null && newOutputData === null) {
        prevOutputDataRef.current = null;
        setOutputData(null);
      }
      return;
    }

    // Skip if it's the same reference (optimization)
    if (prevOutputDataRef.current === newOutputData) {
      return;
    }

    // Deep comparison - only update if stringified content changed
    const newDataStr = JSON.stringify(newOutputData);
    const prevDataStr = prevOutputDataRef.current
      ? JSON.stringify(prevOutputDataRef.current)
      : null;

    if (newDataStr !== prevDataStr) {
      prevOutputDataRef.current = newOutputData;
      setOutputData(newOutputData);
    } else {
      // Content is the same, just update the reference to prevent future comparisons
      prevOutputDataRef.current = newOutputData;
    }
  }, [(data as any).outputData, (data as any).response]);

  // Sync error from node data (set during global workflow execution)
  useEffect(() => {
    const executionStatus = (data as any).executionStatus;
    const error = (data as any).error;

    // If node failed during global execution, show error in ResponseViewer
    if (executionStatus === "failed" && error) {
      setErrorResponse(error);
    } else if (executionStatus === "success") {
      // Clear error when node succeeds
      setErrorResponse(null);
    }
  }, [(data as any).executionStatus, (data as any).error]);

  const refreshInputData = () => {
    const nodes = getNodes();
    const edges = getEdges();
    const connectedInputs = getConnectedInputs(id, edges, nodes);

    setInputData((prevData) => {
      if (JSON.stringify(prevData) === JSON.stringify(connectedInputs)) {
        return prevData;
      }
      return connectedInputs;
    });
  };

  // Get display data for InputViewer
  const getDisplayData = (input: any) => {
    // Priority: response > outputData > configData
    // Both response and outputData need unwrapping logic

    let data = null;

    // First priority: use response if available
    if (input.response && Object.keys(input.response).length > 0) {
      data = input.response;
    } else if (input.outputData) {
      data = input.outputData;
    }

    // Unwrap single-element wrapper arrays if we have data
    if (data) {
      // [{users: [...], total: 208}] → {users: [...], total: 208}
      // But keep multi-element arrays: [{...}, {...}] → [{...}, {...}]
      if (Array.isArray(data) && data.length === 1) {
        data = data[0];
      }

      return data;
    }

    const displayData: any = {};
    const EXCLUDE = [
      "uid",
      "title",
      "description",
      "label",
      "position",
      "nodeType",
      "isDecript",
      "isDecrypted",
      "testConnectionBtn",
      "tabId",
      "configType",
      "onDelete",
      "onDataChange",
      "header",
      "borderColor",
      "shadowColor",
    ];

    // Third priority: extract from configData
    if (input.configData) {
      Object.entries(input.configData)
        .filter(([key]) => !EXCLUDE.includes(key))
        .forEach(([key, value]) => (displayData[key] = value));
    }

    // If still empty, try to use the input object directly (excluding system fields)
    if (Object.keys(displayData).length === 0 && input) {
      Object.entries(input)
        .filter(([key]) => !EXCLUDE.includes(key) && !key.startsWith("_"))
        .forEach(([key, value]) => {
          if (typeof value !== "function") {
            displayData[key] = value;
          }
        });
    }

    return displayData;
  };

  // Prepare available fields for ExpressionEditor autocomplete
  const getAvailableFields = React.useMemo((): Array<{
    path: string;
    value: any;
  }> => {
    const fields: Array<{ path: string; value: any }> = [];

    if (inputData && inputData.length > 0) {
      const firstInput = inputData[0];
      const data = getDisplayData(firstInput);

      // Use 'config' prefix for configuration nodes, '$json' for others
      const prefix = firstInput.configData ? "config" : "$json";

      // Flatten the data structure to expose all nested fields
      flattenObject(data, prefix, fields);
    }

    return fields;
  }, [JSON.stringify(inputData)]);

  const handleDelete = (nodeId: string) => {
    setDeleteId(nodeId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (deleteId && data.onDelete) {
      data.onDelete(deleteId);
    }
    setShowDeleteConfirm(false);
    setDeleteId(null);
  };

  // Add a new condition
  const addCondition = () => {
    const newCondition: Condition = {
      id: `condition_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      leftValue: "",
      rightValue: "",
      operator: {
        type: "",
        operation: "",
      },
    };

    setConditions((prev) => ({
      ...prev,
      conditions: [...prev.conditions, newCondition],
    }));
  };

  // Remove a condition
  const removeCondition = (conditionId: string) => {
    setConditions((prev) => ({
      ...prev,
      conditions: prev.conditions.filter((c) => c.id !== conditionId),
    }));
  };

  // Update condition field
  const updateCondition = (
    conditionId: string,
    field: keyof Condition,
    value: any,
  ) => {
    setConditions((prev) => {
      const newConditions = prev.conditions.map((c) => {
        if (c.id === conditionId) {
          // If leftValue is being changed, automatically update rightValue
          if (field === "leftValue") {
            // If leftValue is empty, clear rightValue
            if (!value || value.trim() === "") {
              return { ...c, [field]: value, rightValue: "" };
            }

            // Extract and resolve values from leftValue expressions
            const values: string[] = [];
            const fieldValues: any[] = [];

            // 1. Handle $json expressions
            const jsonExpressionMatches = value.match(
              /\{\{\s*\$json\.?([^}]+)\}\}/g,
            );

            if (jsonExpressionMatches && inputData?.length) {
              jsonExpressionMatches.forEach((match: string) => {
                // Extract field path from expression (handles nested paths like $json.user.name)
                const fieldPath = match
                  .replace(/\{\{\s*\$json\.?/g, "")
                  .replace(/\s*\}\}/g, "")
                  .trim();

                let foundValue = false;

                // Look up the value in inputData
                for (const input of inputData) {
                  const displayData = getDisplayData(input);

                  if (displayData) {
                    // Handle nested field paths (e.g., "user.name" or "[0].value")
                    let fieldValue;

                    // Direct field access (simple path)
                    if (fieldPath in displayData) {
                      fieldValue = displayData[fieldPath];
                    } else {
                      // Nested path access (handle dots and array indices)
                      try {
                        const pathParts = fieldPath.split(".");
                        fieldValue = pathParts.reduce((obj, key) => {
                          if (!obj) return undefined;

                          // Handle array access like "todo[0]" or just "[0]"
                          const arrayMatch = key.match(/^([^[]*)\[(\d+)\]$/);
                          if (arrayMatch) {
                            const propName = arrayMatch[1]; // "todo" or empty string
                            const index = parseInt(arrayMatch[2]); // 0

                            if (propName) {
                              // Handle "todo[0]" - access obj.todo[0]
                              return obj[propName]?.[index];
                            } else {
                              // Handle "[0]" - access obj[0]
                              return obj[index];
                            }
                          }

                          // Regular property access
                          return obj[key];
                        }, displayData);
                      } catch (e) {
                        console.warn(
                          `Failed to extract value for path: ${fieldPath}`,
                          e,
                        );
                        fieldValue = undefined;
                      }
                    }

                    if (fieldValue !== undefined && fieldValue !== null) {
                      values.push(String(fieldValue));
                      fieldValues.push(fieldValue);
                      foundValue = true;
                      break;
                    }
                  }
                }

                // Fallback: check getAvailableFields if inputData lookup failed
                if (!foundValue) {
                  const matchedField = getAvailableFields.find(
                    (f) =>
                      f.path === `$json.${fieldPath}` ||
                      f.path.endsWith(fieldPath),
                  );
                  if (matchedField?.value !== undefined) {
                    values.push(String(matchedField.value));
                    fieldValues.push(matchedField.value);
                  }
                }
              });
            }

            // 2. Handle $("NodeName") expressions
            const nodeExpressionMatches = value.match(
              /\{\{\s*\$\("([^"]+)"\)\.?([^}]*)\}\}/g,
            );

            if (nodeExpressionMatches) {
              nodeExpressionMatches.forEach((match: string) => {
                const parsed = match.match(
                  /\{\{\s*\$\("([^"]+)"\)\.?([^}]*)\}\}/,
                );
                if (!parsed) return;

                const nodeName = parsed[1];
                const rawPath = parsed[2] || "";

                // Find target node by title (exclude current node)
                const targetNode = nodes.find(
                  (n: any) => n.data?.title === nodeName && n.id !== id,
                );

                if (!targetNode) return;

                // Get outputData
                let rootData =
                  (targetNode as any).data?.outputData ||
                  (targetNode as any).data?.response;
                if (!rootData) return;

                // Unwrap single-element arrays
                if (Array.isArray(rootData) && rootData.length === 1) {
                  rootData = rootData[0];
                }

                // Strip "json" prefix from path
                let actualPath = rawPath.trim();
                if (actualPath === "json" || actualPath === "") {
                  // No scalar to extract, skip
                  return;
                }
                if (actualPath.startsWith("json.")) {
                  actualPath = actualPath.substring(5); // Remove "json."
                } else if (actualPath.startsWith("json[")) {
                  actualPath = actualPath.substring(4); // Remove "json"
                }

                if (!actualPath) return;

                // Traverse the path
                try {
                  const pathParts = actualPath.split(".");
                  const resolvedValue = pathParts.reduce((obj, key) => {
                    if (!obj) return undefined;

                    // Handle array access like "todo[0]" or just "[0]"
                    const arrayMatch = key.match(/^([^[]*)\[(\d+)\]$/);
                    if (arrayMatch) {
                      const propName = arrayMatch[1]; // "todo" or empty string
                      const index = parseInt(arrayMatch[2]); // 0

                      if (propName) {
                        // Handle "todo[0]" - access obj.todo[0]
                        return obj[propName]?.[index];
                      } else {
                        // Handle "[0]" - access obj[0]
                        return obj[index];
                      }
                    }

                    // Regular property access
                    return obj[key];
                  }, rootData);

                  if (resolvedValue !== undefined && resolvedValue !== null) {
                    // Use JSON.stringify for objects, String for primitives
                    if (typeof resolvedValue === "object") {
                      values.push(JSON.stringify(resolvedValue));
                    } else {
                      values.push(String(resolvedValue));
                    }
                    fieldValues.push(resolvedValue);
                  }
                } catch (e) {
                  console.warn(
                    `Failed to extract value from node ${nodeName} at path: ${actualPath}`,
                    e,
                  );
                }
              });
            }

            // After both $json and $() loops, set rightValue and auto-select operator
            if (values.length > 0) {
              const newRightValue = values.join(", ");

              // Auto-select operator if this is a new field (operator not set yet)
              if (!c.operator.type && fieldValues.length > 0) {
                autoSelectOperatorType(conditionId, fieldValues[0]);
              }

              return { ...c, [field]: value, rightValue: newRightValue };
            }

            // If no valid expressions or no input data, just update leftValue
            return { ...c, [field]: value };
          }

          // For other fields, just update normally
          return { ...c, [field]: value };
        }
        return c;
      });

      return {
        ...prev,
        conditions: newConditions,
      };
    });
  };

  // Update operator for a condition
  const updateConditionOperator = (
    conditionId: string,
    dataType: string,
    operationValue: string,
  ) => {
    setConditions((prev) => ({
      ...prev,
      conditions: prev.conditions.map((c) =>
        c.id === conditionId
          ? {
              ...c,
              operator: {
                type: dataType,
                operation: operationValue,
              },
              // Keep rightValue - don't clear it
            }
          : c,
      ),
    }));
  };

  // Auto-select operator type based on dragged field value type
  const autoSelectOperatorType = (conditionId: string, fieldValue: any) => {
    if (!operatorsData?.data) return;

    let detectedType = "";

    // Detect the data type from the field value
    if (typeof fieldValue === "number") {
      detectedType = "number";
    } else if (typeof fieldValue === "string") {
      detectedType = "string";
    } else if (typeof fieldValue === "boolean") {
      detectedType = "boolean";
    } else if (Array.isArray(fieldValue)) {
      detectedType = "array";
    } else if (fieldValue === null || fieldValue === undefined) {
      detectedType = "string"; // Default fallback
    }

    // Find matching data type in operators data
    const matchingDataType = operatorsData.data.find(
      (dt: any) => dt.data_type === detectedType,
    );

    if (matchingDataType && matchingDataType.operator?.length > 0) {
      // Try to find "equals" operator as default, otherwise use first operator
      const equalsOperator = matchingDataType.operator.find(
        (op: any) => op.value === "equals",
      );
      const defaultOperator = equalsOperator || matchingDataType.operator[0];

      updateConditionOperator(
        conditionId,
        matchingDataType.data_type,
        defaultOperator.value,
      );
    }
  };

  // Prepare conditions for payload - always include rightValue
  const prepareConditionsForPayload = (conditionsData: any) => {
    const preparedConditions = {
      ...conditionsData,
      conditions: conditionsData.conditions.map((condition: Condition) => {
        // Find the operator to check if it requires input
        const selectedDataType = operatorsData?.data?.find(
          (dt: any) => dt.data_type === condition.operator.type,
        );
        const selectedOperator = selectedDataType?.operator?.find(
          (op: any) => op.value === condition.operator.operation,
        );

        const requiresInput = selectedOperator?.requires_input !== false;

        // Always include rightValue - set to empty string if operator doesn't require input
        return {
          ...condition,
          rightValue: requiresInput ? condition.rightValue || "" : "",
        };
      }),
    };

    return preparedConditions;
  };

  // Execute workflow
  const executeIfElse = async () => {
    if (!inputData?.length) {
      toast({
        variant: "destructive",
        title: "No Input Data",
        description: "Connect a node to provide input data",
      });
      return;
    }

    if (!title.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Title is required for the If Else node",
        duration: 3000,
      });
      return;
    }

    // Get active mesh flow from sessionStorage
    const activeMeshFlowStr = sessionStorage.getItem("activeMeshFlow");
    if (!activeMeshFlowStr) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          "No active mesh flow. Please create or select a mesh flow first.",
        duration: 4000,
      });
      return;
    }

    setOutputData(null);
    setErrorResponse(null);
    setLoading(true);

    try {
      const activeMeshFlow = JSON.parse(activeMeshFlowStr);

      // Get plugin ID from session
      const pluginId = sessionStorage.getItem("editPluginId") || "";

      // Get all nodes and edges from React Flow
      const allNodes = reactFlowInstance.getNodes();
      const allEdges = reactFlowInstance.getEdges();

      // Get execution path: only nodes from start to current node (no downstream nodes)
      const executableNodes = getExecutionPath(id, allNodes, allEdges);

      // Mark all executable nodes as pending
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          // Check if this node is in the execution path
          const isExecutable = executableNodes.some(
            (n: any) => n.id === node.id,
          );
          if (isExecutable) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "pending",
                executionResult: undefined,
                error: undefined,
              },
            };
          }
          return node;
        }),
      );

      // Build nodes array for workflow
      const workflowNodes = executableNodes.map((node) => {
        let nodeType = "core";
        let subType = "http_request";
        let parameters;

        if (node.id === id) {
          // This is the current IfElseNode - build from local state
          const currentConditions = {
            ...conditions,
            combinator,
          };

          const nodeParameters = {
            conditions: prepareConditionsForPayload(currentConditions),
            looseTypeValidation: looseTypeValidation,
            options: data.options || {},
          };

          nodeType = "flow";
          subType = "if_else";
          parameters = {
            ...nodeParameters,
            label: label,
          };
        } else {
          // Other nodes - use centralized utility (checks storedParameters first)
          // Determine backend type and sub_type using centralized mapping
          const backendTypeInfo = getNodeBackendType(node.type || "custom");
          nodeType = backendTypeInfo.type;
          subType = backendTypeInfo.sub_type;

          // Special handling for ifElseNode with conditions
          if (node.type === "ifElseNode") {
            // For other IfElseNodes, also prepare conditions
            const otherConditions = node.data.conditions || {
              options: {
                caseSensitive: true,
                leftValue: "",
                typeValidation: "strict",
                version: 3,
              },
              conditions: [],
              combinator: "and",
            };
            const nodeParameters = {
              conditions: prepareConditionsForPayload(otherConditions),
              looseTypeValidation: node.data.looseTypeValidation || false,
              options: node.data.options || {},
            };
            parameters = {
              ...nodeParameters,
              label: node.data.label,
            };
          } else if (node.data.configData) {
            // Authentication/configuration nodes
            parameters = getNodeExecutionParameters(node, pluginId).parameters;
          } else if (node.data.sub_type) {
            // Nodes with explicit sub_type
            parameters = getNodeExecutionParameters(node, pluginId).parameters;
          } else {
            // All other nodes
            parameters = getNodeExecutionParameters(node, pluginId).parameters;
          }
        }

        return {
          uid: node.data.uid || node.id,
          title: node.data.title || node.data.label || "Untitled",
          type: nodeType,
          sub_type: subType,
          version: "1.0",
          position: [node.position.x, node.position.y],
          parameters,
        };
      });

      // Create mapping from React Flow node IDs to UIDs
      const nodeIdToUidMap: Record<string, string> = {};
      executableNodes.forEach((node) => {
        const nodeUid = node.data.uid || node.id;
        nodeIdToUidMap[node.id] = nodeUid;
      });

      // Build connections object from edges - only in execution path
      // Create Set of node IDs in execution path for filtering
      const executionPathNodeIds = new Set(executableNodes.map((n) => n.id));

      const connections: any = {};
      allEdges
        .filter(
          (edge) =>
            executionPathNodeIds.has(edge.source) &&
            executionPathNodeIds.has(edge.target),
        )
        .forEach((edge) => {
          const sourceUid = nodeIdToUidMap[edge.source];
          const targetUid = nodeIdToUidMap[edge.target];

          if (!sourceUid || !targetUid) return;

          if (!connections[sourceUid]) {
            connections[sourceUid] = {
              main: [],
            };
          }

          // Check if source node is an if_else node
          const sourceNode = executableNodes.find((n) => n.id === edge.source);
          const isIfElseNode =
            sourceNode?.type === "ifElseNode" ||
            sourceNode?.data?.sub_type === "if_else";

          // For if_else nodes, route to different output arrays based on handle
          let outputIndex = 0;
          if (isIfElseNode && edge.sourceHandle) {
            // true handle → main[0], false handle → main[1]
            outputIndex = edge.sourceHandle === "false" ? 1 : 0;
          }

          if (!connections[sourceUid].main[outputIndex]) {
            connections[sourceUid].main[outputIndex] = [];
          }

          connections[sourceUid].main[outputIndex].push({
            node: targetUid,
            type: "main",
            index: 0,
          });
        });

      // Build complete workflow payload
      const workflowPayload = buildWorkflowPayload(
        activeMeshFlow,
        workflowNodes,
        connections,
      );

      const result = await executeWorkflow(workflowPayload);

      // Update ALL executed nodes with their results (not just the clicked node)
      if (result?.results?.[0]?.node_results) {
        const nodeResults = result.results[0].node_results;

        // Use the utility function to handle node execution results
        const { hasErrors, errorMessage, errorNodeTitle } =
          handleNodeExecutionResults(reactFlowInstance, nodeResults, allEdges);

        // Extract this node's output for local state
        const resultsMap = new Map<string, any>();
        nodeResults.forEach((nodeResult: any) => {
          resultsMap.set(nodeResult.uid, nodeResult);
        });

        const currentNodeResult = resultsMap.get(uid || id);
        if (currentNodeResult) {
          // Check if this node failed
          const isFailed =
            currentNodeResult.status === "error" ||
            currentNodeResult.status === "failed" ||
            currentNodeResult.status === "failure";

          if (isFailed) {
            // Show error in ResponseViewer but don't pass to downstream nodes
            setErrorResponse(currentNodeResult.error || "Execution failed");
            setOutputData(null);
          } else if (currentNodeResult.output_data) {
            // Success - extract and display output data
            const rawOutputData = currentNodeResult.output_data;
            const responseData = unwrapJsonData(currentNodeResult.output_data);

            setOutputData(responseData);
            setErrorResponse(null);

            // Calculate item counts from RAW output data (before unwrapping)
            // If/Else returns [[trueItems], [falseItems]] in raw format
            let trueCount = 0;
            let falseCount = 0;

            if (rawOutputData && Array.isArray(rawOutputData)) {
              if (
                rawOutputData.length >= 1 &&
                Array.isArray(rawOutputData[0])
              ) {
                trueCount = rawOutputData[0].length;
              }
              if (
                rawOutputData.length >= 2 &&
                Array.isArray(rawOutputData[1])
              ) {
                falseCount = rawOutputData[1].length;
              }
            }

            // Update node_data with calculated counts (will trigger edge update via useEffect)
            const updatedNodeData = [
              { id: "true", outputKey: "True", itemCount: trueCount },
              { id: "false", outputKey: "False", itemCount: falseCount },
            ];
            setNodeData(updatedNodeData);

            // Update parent with node_data AND storedParameters so save captures the counts
            if (data.onDataChange) {
              const currentConditions = { ...conditions, combinator };
              data.onDataChange(id, {
                response: responseData,
                outputData: responseData,
                node_data: updatedNodeData,
                storedParameters: {
                  conditions: currentConditions,
                  looseTypeValidation,
                  options: data.options || {},
                  node_data: updatedNodeData,
                },
              });
            }
          }
        }

        // Show appropriate toast based on execution result
        if (hasErrors) {
          // Show error toast with specific error message
          const errorDescription = errorMessage
            ? `${errorNodeTitle}: ${errorMessage}`
            : "One or more nodes failed during execution";

          const shortError =
            errorDescription.length > 150
              ? errorDescription.substring(0, 150) + "..."
              : errorDescription;

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description: shortError,
            duration: 7000,
          });
        } else {
          // Only show success if ALL nodes succeeded
          toast({
            variant: "success",
            title: "Success",
            description: "If/Else executed successfully",
            duration: 3000,
          });
        }
      } else {
        // Fallback - no node results in response
        // Check for success at top level
        const isSuccess =
          result?.success === true ||
          result?.status === "success" ||
          result?.body?.status === 1 ||
          result?.body?.status === "1" ||
          result?.status === 1;

        if (isSuccess) {
          // Mark current node as success
          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "success",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "success",
            title: "Success",
            description: result?.message || "If/Else executed successfully",
            duration: 3000,
          });
        } else {
          // Mark current node and all downstream nodes as failed
          const allEdges = reactFlowInstance.getEdges();
          const downstreamNodes = findDownstreamNodes(id, allEdges);

          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error:
                      result?.body?.message ||
                      result?.message ||
                      "Execution failed",
                  },
                };
              } else if (downstreamNodes.has(node.id)) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error: "Not executed due to upstream failure",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description:
              result?.body?.message ||
              result?.message ||
              "If/Else execution failed",
            duration: 4000,
          });
        }
      }
    } catch (error: any) {
      console.error("If/Else Execution Error:", error);
      setErrorResponse(error?.message || "If/Else execution failed");
      setOutputData(null);

      // Get all edges for finding downstream nodes
      const allEdges = reactFlowInstance.getEdges();
      const downstreamNodes = findDownstreamNodes(id, allEdges);

      // Update current node and all downstream nodes to failed status
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            // Mark the failed node as failed
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: error?.message || "If/Else execution failed",
              },
            };
          } else if (downstreamNodes.has(node.id)) {
            // Mark downstream nodes as failed (not executed due to upstream failure)
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: "Not executed due to upstream failure",
              },
            };
          }
          return node;
        }),
      );

      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message || "If/Else execution failed",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Copy output to clipboard
  const handleCopyOutput = () => {
    const dataToCopy = errorResponse || outputData;
    if (!dataToCopy) return;
    navigator.clipboard.writeText(JSON.stringify(dataToCopy, null, 2));
    toast({
      variant: "success",
      title: "Copied!",
      description: "Output copied to clipboard.",
      duration: 2000,
    });
  };

  // Render input data section
  const renderInputData = () => {
    if (!inputData?.length) {
      return (
        <div className="text-xs text-muted-foreground p-4 border rounded-md bg-muted/20 text-center">
          <div className="font-medium">No Input Connected</div>
          <div>Connect a node to see data here</div>
        </div>
      );
    }

    // Build sources array for PaginatedInputViewer (handles tabs internally)
    const sources = inputData.map((input, index) => {
      // Generate label: if from switch node, use "Rule X", otherwise use original label
      let displayLabel = input.label || `Source ${index + 1}`;

      // Check if label indicates switch node (e.g., "switch", "SWITCH")
      if (displayLabel.toLowerCase().includes("switch")) {
        displayLabel = `Rule ${index + 1}`;
      }

      return {
        label: displayLabel,
        data: getDisplayData(input),
        isConfig: !!input.configData, // Mark as config if source is configuration
      };
    });

    return (
      <PaginatedInputViewer
        sources={sources}
        onRefresh={refreshInputData}
        sourceCount={inputData.length}
      />
    );
  };

  // Helper function to get icon for data type
  const getDataTypeIcon = (dataType: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      number: <Hash className="h-3.5 w-3.5 mr-2" />,
      string: <Type className="h-3.5 w-3.5 mr-2" />,
      boolean: <ToggleLeft className="h-3.5 w-3.5 mr-2" />,
      date: <Calendar className="h-3.5 w-3.5 mr-2" />,
      object: <Braces className="h-3.5 w-3.5 mr-2" />,
      array: <List className="h-3.5 w-3.5 mr-2" />,
      dateTime: <Clock className="h-3.5 w-3.5 mr-2" />,
    };

    return iconMap[dataType] || <Type className="h-3.5 w-3.5 mr-2" />;
  };

  // Check if all conditions are properly filled
  const areConditionsValid = () => {
    if (conditions.conditions.length === 0) return false;

    return conditions.conditions.every((condition) => {
      // Check if leftValue is filled
      if (!condition.leftValue || !condition.leftValue.trim()) return false;

      // Check if operator is selected
      if (!condition.operator.type || !condition.operator.operation)
        return false;

      // Check if rightValue is filled when required
      const selectedDataType = operatorsData?.data?.find(
        (dt: any) => dt.data_type === condition.operator.type,
      );
      const selectedOperator = selectedDataType?.operator?.find(
        (op: any) => op.value === condition.operator.operation,
      );
      const requiresInput = selectedOperator?.requires_input !== false;

      if (
        requiresInput &&
        (!condition.rightValue || !condition.rightValue.trim())
      ) {
        return false;
      }

      return true;
    });
  };

  return (
    <FullscreenWrapper
      id={id}
      isExpanded={isExpanded}
      executionStatus={(data as any).executionStatus}
      borderColor={data.borderColor}
      shadowColor={data.shadowColor}
      onContextMenu={handleContextMenu}
      expandedClassName="w-[90vw] !max-w-[1000px] flex flex-col"
      collapsedStyle={{ height: "100px" }}
    >
      <Confirmation
        text="Are you sure you want to delete this node?"
        confirmAction={confirmDelete}
        rejectAction={() => setShowDeleteConfirm(false)}
        confirmText="Confirm"
        rejectText="Cancel"
        show={showDeleteConfirm}
        closeDialog={() => setShowDeleteConfirm(false)}
      />

      {/* Context Menu */}
      <NodeContextMenu
        show={showContextMenu}
        position={contextMenuPosition}
        onClose={closeContextMenu}
        items={[
          createContextMenuItems.expandCollapse(isExpanded, () =>
            setIsExpanded((prev) => !prev),
          ),
          createContextMenuItems.delete(
            () => handleDelete(id),
            !!data.onDelete,
          ),
        ]}
      />

      {/* Handles */}
      <Handle
        id="input"
        type="target"
        position={Position.Left}
        className={handlerStyle}
      />

      {/* Output handles with labels - exact Switch pattern */}
      {(() => {
        const handles = [
          {
            id: "true",
            label: "True",
            top: 30,
            color: "var(--primaryGreen, #22c55e)",
            itemCount: nodeData[0]?.itemCount ?? 0,
          },
          {
            id: "false",
            label: "False",
            top: 70,
            color: "var(--primary)",
            itemCount: nodeData[1]?.itemCount ?? 0,
          },
        ];

        return handles.map(({ id: hId, label, top, color, itemCount }) => (
          <div key={hId}>
            <Handle
              id={hId}
              type="source"
              position={Position.Right}
              style={{ top: `${top}%` }}
              className="!bg-background !w-4 !h-4 rounded-full !-right-2 !border-2 !border-muted-foreground"
            />
            {!isExpanded && (
              <div
                className="absolute bg-background rounded-xl px-1 text-[8px] font-semibold whitespace-nowrap pointer-events-none"
                style={{
                  top: `${top}%`,
                  transform: "translateY(-50%)",
                  left: "calc(100% + 8px)",
                  color,
                }}
              >
                {label}
                {itemCount > 0 && (
                  <span className="ml-0.5 opacity-70">({itemCount})</span>
                )}
              </div>
            )}
          </div>
        ));
      })()}
      {/* Header */}
      <div
        className={`flex items-center justify-between gap-4 rounded-md transition-all duration-500 ${
          isExpanded ? " p-3" : "!p-3"
        }`}
      >
        <CollapsedNodeView
          icon={<Split className="rotate-90" />}
          label={label}
          title={title}
          iconSize={35}
          borderColor={data.borderColor}
          onToggle={() => setIsExpanded((prev) => !prev)}
          isExpanded={isExpanded}
          executionStatus={(data as any).executionStatus}
          description={description}
        />
        <FullscreenToggleButton isExpanded={isExpanded} />
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Metadata Form */}
          <div className="mt-2 p-4 border-t flex items-start gap-4 flex-shrink-0">
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Title <span className="text-primary">*</span>
              </label>
              <ValidatedTitleInput
                id={id}
                title={title}
                onChange={setTitle}
                nodes={nodes}
                dataTitle={data.title}
                onValidationChange={setIsTitleValid}
              />
            </div>
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter node description"
                className="w-full p-2 border rounded text-xs resize-none text-muted-foreground bg-background"
                rows={2}
              />
            </div>
          </div>

          {/* 3 Section Grid */}
          <div
            ref={containerRef}
            className="flex gap-0 p-4 border-t flex-1 min-h-0 overflow-hidden"
          >
            {/* Left Section - Input Data (4/12 columns) */}
            <div
              style={{ width: `${widths[0]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={true} hasRelative={true}>
                {renderInputData()}
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(0)} />

            {/* Middle Section - Logic (4/12 columns) */}
            <div
              style={{ width: `${widths[1]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection hasRelative={true} scrollable={false}>
                {/* Single scroll surface — wraps all scrollable content */}
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-2 relative">
                  <h3 className="text-sm font-semibold sticky top-0 bg-background z-10 pb-2 border-b">
                    Parameters
                  </h3>

                  {/* Condition Builder - Scrollable area */}
                  <div
                    className="flex-1 overflow-y-auto pr-2 space-y-3"
                    style={{ maxHeight: "calc(100% - 80px)" }}
                  >
                    {operatorsLoading ? (
                      <div className="p-8 text-center border rounded-md bg-muted/10">
                        <div className="text-sm text-muted-foreground">
                          Loading operators...
                        </div>
                      </div>
                    ) : operatorsError ? (
                      <div className="p-8 text-center border rounded-md bg-muted/10">
                        <div className="text-sm text-destructive">
                          Failed to load operators
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Conditions List */}
                        <div className="space-y-2">
                          {conditions.conditions.map((condition, index) => (
                            <React.Fragment key={condition.id}>
                              <div className="border rounded-md p-3 bg-muted/20 space-y-2">
                                {/* Condition Header */}
                                <div className="flex items-center justify-between">
                                  <label className="text-xs font-semibold">
                                    Condition {index + 1}
                                  </label>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      removeCondition(condition.id)
                                    }
                                    className="h-6 w-6 p-0 hover:bg-destructive/10"
                                  >
                                    <Trash2 className="h-3 w-3 text-destructive" />
                                  </Button>
                                </div>

                                {/* Left Value Input */}
                                <div className="space-y-1">
                                  <label className="text-xs text-muted-foreground">
                                    Left Value
                                  </label>
                                  <ExpressionEditor
                                    value={condition.leftValue}
                                    onChange={(value) =>
                                      updateCondition(
                                        condition.id,
                                        "leftValue",
                                        value,
                                      )
                                    }
                                    placeholder="Type {{$ for options or drag fields"
                                    availableFields={getAvailableFields}
                                    nodes={nodes}
                                    currentNodeId={id}
                                    minHeight="32px"
                                    maxHeight="120px"
                                  />
                                </div>

                                {/* Operator Dropdown */}
                                <div className="space-y-1">
                                  <label className="text-xs text-muted-foreground">
                                    Operator
                                  </label>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="outline"
                                        className="w-full h-8 justify-between text-xs font-normal"
                                      >
                                        <span className="truncate">
                                          {condition.operator.operation
                                            ? `${condition.operator.type} - ${condition.operator.operation}`
                                            : "Select operator"}
                                        </span>
                                        <ChevronRight className="h-3 w-3 ml-2 shrink-0" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className="w-56">
                                      {operatorsData?.data?.map(
                                        (dataType: any) => {
                                          const isSelectedType =
                                            condition.operator.type ===
                                            dataType.data_type;
                                          return (
                                            <DropdownMenuSub
                                              key={dataType.data_type}
                                            >
                                              <DropdownMenuSubTrigger
                                                className={`text-xs ${isSelectedType ? "text-primary font-medium" : ""}`}
                                              >
                                                <div className="flex items-center">
                                                  {getDataTypeIcon(
                                                    dataType.data_type,
                                                  )}
                                                  <span>
                                                    {dataType.display_name}
                                                  </span>
                                                </div>
                                              </DropdownMenuSubTrigger>
                                              <DropdownMenuPortal>
                                                <DropdownMenuSubContent>
                                                  {dataType.operator?.map(
                                                    (op: any) => {
                                                      const isSelected =
                                                        condition.operator
                                                          .operation ===
                                                          op.value &&
                                                        isSelectedType;
                                                      return (
                                                        <DropdownMenuItem
                                                          key={op.value}
                                                          className={`text-xs ${isSelected ? "text-primary font-medium bg-primary/10" : ""}`}
                                                          onClick={() =>
                                                            updateConditionOperator(
                                                              condition.id,
                                                              dataType.data_type,
                                                              op.value,
                                                            )
                                                          }
                                                        >
                                                          {op.display_name}
                                                        </DropdownMenuItem>
                                                      );
                                                    },
                                                  )}
                                                </DropdownMenuSubContent>
                                              </DropdownMenuPortal>
                                            </DropdownMenuSub>
                                          );
                                        },
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>

                                {/* Right Value - Auto-populated from left value */}
                                {(() => {
                                  // Find the selected operator from API data
                                  const selectedDataType =
                                    operatorsData?.data?.find(
                                      (dt: any) =>
                                        dt.data_type ===
                                        condition.operator.type,
                                    );
                                  const selectedOperator =
                                    selectedDataType?.operator?.find(
                                      (op: any) =>
                                        op.value ===
                                        condition.operator.operation,
                                    );

                                  // Determine if input is required (editable) or not (disabled)
                                  const requiresInput =
                                    selectedOperator?.requires_input !== false;

                                  // Check if we have expressions in left value
                                  const hasExpressions =
                                    condition.leftValue &&
                                    /\{\{\s*\$json\.[^}]+\}\}/.test(
                                      condition.leftValue,
                                    );

                                  return (
                                    <div className="space-y-1">
                                      <label className="text-xs text-muted-foreground">
                                        Right Value
                                      </label>
                                      <Input
                                        value={condition.rightValue || ""}
                                        disabled={!requiresInput}
                                        onChange={(e) =>
                                          updateCondition(
                                            condition.id,
                                            "rightValue",
                                            e.target.value,
                                          )
                                        }
                                        placeholder={
                                          requiresInput
                                            ? hasExpressions
                                              ? "Extracting values..."
                                              : "Drag fields to left value"
                                            : "Not required for this operator"
                                        }
                                        className={`text-xs h-8 font-mono ${
                                          !requiresInput
                                            ? "bg-muted/30 cursor-not-allowed"
                                            : ""
                                        }`}
                                      />
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* Combinator between conditions - only show if not the last condition */}
                              {index < conditions.conditions.length - 1 && (
                                <div className="flex items-center justify-center py-2">
                                  <Select
                                    value={combinator}
                                    onValueChange={(value: "and" | "or") =>
                                      setCombinator(value)
                                    }
                                  >
                                    <SelectTrigger className="w-24 h-7 text-xs font-semibold">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectGroup>
                                        <SelectItem
                                          value="and"
                                          className="text-xs"
                                        >
                                          AND
                                        </SelectItem>
                                        <SelectItem
                                          value="or"
                                          className="text-xs"
                                        >
                                          OR
                                        </SelectItem>
                                      </SelectGroup>
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>

                        {/* Add Condition Button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={addCondition}
                          className="w-full"
                        >
                          <Plus className="h-3 w-3 mr-2" />
                          Add Condition
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                {/* Execute Button - Sticky at bottom */}
                <div className="mt-auto pt-3 border-t bg-background sticky bottom-0">
                  <Button
                    onClick={executeIfElse}
                    disabled={
                      !inputData ||
                      inputData.length === 0 ||
                      !areConditionsValid() ||
                      !title.trim() ||
                      operatorsLoading ||
                      !isTitleValid
                    }
                    className="w-full"
                    size="sm"
                  >
                    <FlaskConical size={14} className="mr-2" />
                    Execute step
                  </Button>
                </div>
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(1)} />

            {/* Right Section - Response (4/12 columns) */}
            <div style={{ flex: 1, minWidth: 80 }}>
              <NodeContentSection scrollable={false} hasRelative={true}>
                <ResponseViewer
                  response={errorResponse || outputData}
                  loading={loading}
                  emptyMessage="Execute If Else to see output preview"
                  loadingMessage="Processing..."
                  onCopy={handleCopyOutput}
                  isError={!!errorResponse}
                  isLoopChild={(data as any).is_loop_child || false}
                  totalIterations={(data as any).total_iterations || 0}
                  iterations={(data as any).iterations || []}
                  fillParent={true}
                  onResponseChange={(newData) => {
                    setOutputData(newData);
                    data.onDataChange?.(id, {
                      response: newData,
                      manuallyEdited: true,
                    });
                  }}
                />
              </NodeContentSection>
            </div>
          </div>
        </div>
      )}
    </FullscreenWrapper>
  );
};

export default IfElseNode;
